import React from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import TopBar from './TopBar';
import Header from './Header';
import Footer from './Footer';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: { label: string; path: string }[];
  banner?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, breadcrumbs, banner }) => {
  return (
    <div 
      className={`relative ${!banner ? 'bg-gradient-to-br from-primary-950 via-primary-900 to-primary-950' : ''}`}
    >
      {/* Banner Background */}
      {banner && (
        <>
          <div className="absolute inset-0">
            <img
              src={banner}
              alt={title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50" />
          </div>
        </>
      )}

      {/* Content */}
      <div className="container-custom h-[140px] flex items-center relative z-10">
        <div className="w-full">
          {/* Breadcrumbs */}
          {breadcrumbs && (
            <nav className="mb-3">
              <ol className="flex items-center space-x-2 text-sm text-gray-300">
                {breadcrumbs.map((item, index) => (
                  <li key={`breadcrumb-${index}-${item.path}`} className="flex items-center">
                    {index > 0 && (
                      <ChevronRight className="w-4 h-4 mx-2 text-gray-400" />
                    )}
                    <Link
                      to={item.path}
                      className={`hover:text-white transition-colors ${
                        index === breadcrumbs.length - 1
                          ? 'text-white font-medium'
                          : ''
                      }`}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {/* Title and Subtitle */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 leading-tight">
                {title}
              </h1>
              {subtitle && (
                <p className="text-lg text-gray-300">
                  {subtitle}
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Layout: React.FC = () => {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Header - Fixed at top */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <TopBar />
        <Header />
      </header>
      
      {/* Main Content - Pushed down by header height */}
      <main className="flex-1 mt-[104px] flex flex-col">
        <div className="flex-1 flex flex-col">
          <Outlet />
        </div>
      </main>
      
      {/* Footer - Always at bottom */}
      <footer className="mt-auto">
        <Footer />
      </footer>
    </div>
  );
};

export default Layout;